import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, projectsAPI, streamsAPI } from '../lib/api';
import { 
  FolderKanban, 
  Plane, 
  Users, 
  Link2, 
  AlertTriangle,
  Radio,
  Maximize2,
  Plus,
  Trash2,
  Clock,
  TrendingUp,
  MapPin,
  Shield,
  BarChart3,
  Trophy,
  Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import FullscreenPlayer from '../components/FullscreenPlayer';

// ─── Horizontal Bar Chart ───
const HBarChart = ({ data, valueKey, labelKey, color = '#C8A400', suffix = '' }) => {
  const maxVal = Math.max(...data.map(d => d[valueKey]), 1);
  return (
    <div className="space-y-2.5">
      {data.map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-xs text-gray-400 font-mono w-28 truncate text-right" title={item[labelKey]}>{item[labelKey]}</span>
          <div className="flex-1 h-6 bg-white rounded-md overflow-hidden relative">
            <div
              className="h-full rounded-md transition-all duration-700 ease-out"
              style={{ width: `${Math.max((item[valueKey] / maxVal) * 100, 2)}%`, backgroundColor: color, animationDelay: `${i * 0.08}s` }}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono font-bold text-gray-600">
              {item[valueKey]}{suffix}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Mini Vertical Bar Chart ───
const MiniBarChart = ({ data, valueKey, labelKey, color = '#C8A400' }) => {
  const maxVal = Math.max(...data.map(d => d[valueKey]), 1);
  return (
    <div className="flex items-end gap-2 h-28 px-2">
      {data.map((item, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <span className="text-[10px] text-gray-500 font-mono font-bold">{item[valueKey] > 0 ? item[valueKey] : ''}</span>
          <div
            className="w-full rounded-t transition-all duration-500"
            style={{
              height: `${Math.max((item[valueKey] / maxVal) * 100, item[valueKey] > 0 ? 6 : 0)}%`,
              backgroundColor: color,
              minHeight: item[valueKey] > 0 ? '4px' : '0'
            }}
          />
          <span className="text-[10px] text-gray-500 font-mono truncate w-full text-center">{item[labelKey]}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Stat Card ───
const StatCard = ({ icon: Icon, label, value, color = "gold", delay = 0, sub }) => (
  <div className="card-hud p-4 animate-slide-up" style={{ animationDelay: `${delay}s` }} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center`}>
        <Icon size={20} className={`text-${color}`} />
      </div>
      <div>
        <p className="text-gray-400 text-xs font-mono uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-rajdhani font-bold text-navy">{value}</p>
        {sub && <p className="text-gray-500 text-[10px] font-mono">{sub}</p>}
      </div>
    </div>
  </div>
);

// ─── Alert Item ───
const AlertItem = ({ item, type }) => (
  <div className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg animate-scale-in">
    <div className="flex items-center gap-3">
      <AlertTriangle size={16} className="text-red-500" />
      <div>
        <p className="text-gray-200 text-sm font-medium">{type === 'drone' ? item.serial_number : item.name}</p>
        <p className="text-gray-500 text-xs font-mono">
          Expira: {new Date(type === 'drone' ? item.insurance_expiry : item.contract_expiry).toLocaleDateString('pt-BR')}
        </p>
      </div>
    </div>
    <span className="badge-danger">VENCENDO</span>
  </div>
);

// ─── Video Frame ───
const VideoFrame = ({ iframe, index, onExpand, projectName, onDelete, canDelete }) => (
  <div className="card-hud overflow-hidden relative group animate-scale-in" style={{ animationDelay: `${index * 0.1}s` }} data-testid={`video-frame-${index}`}>
    <div className="aspect-video">
      <iframe src={iframe.url} className="w-full h-full rounded-t-lg" frameBorder="0" allowFullScreen title={iframe.name} />
    </div>
    <div className="px-3 py-2 flex items-center justify-between bg-white border-t border-gray-100">
      <div className="min-w-0 flex-1">
        <p className="text-navy text-sm font-medium truncate">{iframe.name}</p>
        {projectName && <p className="text-gray-400 text-[10px] font-mono truncate">{projectName}</p>}
      </div>
      <div className="flex gap-1 flex-shrink-0">
        {canDelete && (
          <Button variant="ghost" size="icon" className="text-gray-300 hover:text-red-500 hover:bg-red-50 h-7 w-7" onClick={() => onDelete(iframe)} data-testid={`delete-stream-btn-${index}`}>
            <Trash2 size={14} />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="text-gray-300 hover:text-gold hover:bg-gold/10 h-7 w-7" onClick={() => onExpand(iframe)} data-testid={`expand-btn-${index}`}>
          <Maximize2 size={14} />
        </Button>
      </div>
    </div>
  </div>
);

// ─── Pilot Row ───
const PilotRow = ({ pilot, rank }) => {
  const medals = ['', 'bg-amber-50 text-amber-700 border-amber-200', 'bg-gray-100 text-gray-400 border-gray-300', 'bg-orange-50 text-orange-600 border-orange-200'];
  const medalClass = medals[rank] || '';
  return (
    <div className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-100 transition-colors">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${medalClass || 'bg-white text-gray-500 border-gray-300'}`}>
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-gray-200 text-sm font-medium truncate">{pilot.name}</p>
        <p className="text-gray-500 text-[10px] font-mono">{pilot.hours}h | {pilot.km} km</p>
      </div>
      <div className="text-right">
        <p className="text-gold font-rajdhani font-bold text-lg">{pilot.flights}</p>
        <p className="text-gray-400 text-[10px] font-mono">voos</p>
      </div>
    </div>
  );
};

// ══════════════════════ MAIN COMPONENT ══════════════════════

const DashboardPage = () => {
  const { isSuperAdmin, isAdmin, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [iframes, setIframes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [showAddStream, setShowAddStream] = useState(false);
  const [newStreamName, setNewStreamName] = useState('');
  const [newStreamUrl, setNewStreamUrl] = useState('');
  const [periodDays, setPeriodDays] = useState(null); // null = all

  const fetchData = async (days) => {
    try {
      const [statsRes, metricsRes] = await Promise.all([
        dashboardAPI.stats(),
        dashboardAPI.metrics(days)
      ]);
      setStats(statsRes.data);
      setMetrics(metricsRes.data);

      if (isSuperAdmin) {
        const streamsRes = await streamsAPI.list();
        setIframes(streamsRes.data.map(s => ({ ...s, projectName: null })));
      } else if (isAdmin) {
        const projectsRes = await projectsAPI.list();
        const allIframes = [];
        for (const proj of projectsRes.data) {
          (proj.players || []).forEach(player => {
            allIframes.push({ url: player.url, name: player.name || 'Stream', projectName: proj.name });
          });
        }
        setIframes(allIframes);
      } else if (user?.project_id) {
        const projectRes = await projectsAPI.get(user.project_id);
        setIframes((projectRes.data.players || []).map(p => ({ url: p.url, name: p.name, projectName: projectRes.data.name })));
      }
    } catch (error) {
      console.error(error);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(periodDays); }, [isSuperAdmin, isAdmin, periodDays]);

  const handlePeriodChange = (days) => {
    setPeriodDays(days);
  };

  const handleAddStream = async (e) => {
    e.preventDefault();
    if (!newStreamUrl) { toast.error('URL obrigatoria'); return; }
    try {
      await streamsAPI.create({ name: newStreamName || 'Stream', url: newStreamUrl });
      toast.success('Stream adicionado!');
      setShowAddStream(false);
      setNewStreamName('');
      setNewStreamUrl('');
      fetchData(periodDays);
    } catch (error) { toast.error('Erro ao adicionar stream'); }
  };

  const handleDeleteStream = async (stream) => {
    if (!stream.id) { toast.error('ID do stream nao encontrado'); return; }
    if (!window.confirm(`Excluir stream "${stream.name}"?`)) return;
    try {
      await streamsAPI.delete(stream.id);
      toast.success('Stream excluido!');
      fetchData(periodDays);
    } catch (error) { toast.error('Erro ao excluir stream'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  const showMetrics = (isSuperAdmin || isAdmin) && metrics;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* ─── Top Stats Cards ─── */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={FolderKanban} label="Projetos" value={stats.projects_count} color="gold" delay={0} />
          <StatCard icon={Plane} label="Drones" value={stats.drones_count} color="ice-blue" delay={0.1} />
          <StatCard icon={Users} label="Usuarios" value={stats.users_count} color="signal-green" delay={0.2} />
          <StatCard icon={Link2} label="Links Ativos" value={stats.active_links} color="accent-cyan" delay={0.3} />
        </div>
      )}

      {/* ─── Period Filter + Metrics KPIs ─── */}
      {showMetrics && (
        <div className="space-y-4" data-testid="metrics-section">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
              <BarChart3 size={20} className="text-gold" /> METRICAS OPERACIONAIS
            </h2>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-testid="period-filter">
              {[
                { label: '7D', value: 7 },
                { label: '30D', value: 30 },
                { label: '90D', value: 90 },
                { label: 'TUDO', value: null },
              ].map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => handlePeriodChange(opt.value)}
                  className={`px-3 py-1.5 text-xs font-mono font-bold rounded-md transition-all duration-200 ${
                    periodDays === opt.value
                      ? 'bg-gold text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200'
                  }`}
                  data-testid={`period-${opt.label.toLowerCase()}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="metrics-kpis">
            <StatCard icon={Clock} label="Horas Voadas" value={`${metrics.total_hours}h`} color="gold" delay={0.1} sub={`${metrics.total_minutes} min total`} />
            <StatCard icon={Activity} label="Voos no Periodo" value={metrics.total_flights} color="signal-green" delay={0.15} sub={`${metrics.month_flights} neste mes`} />
            <StatCard icon={MapPin} label="KM Total" value={metrics.total_km} color="ice-blue" delay={0.2} />
            <StatCard icon={Plane} label="Drones Ativos" value={metrics.active_drones} color="accent-cyan" delay={0.25} />
          </div>
        </div>
      )}

      {/* ─── Analytics Grid ─── */}
      {showMetrics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="metrics-charts">
          {/* Hours by Project */}
          <div className="card-hud p-5 lg:col-span-2 animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <h3 className="text-navy font-rajdhani font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
              <BarChart3 size={18} className="text-gold" /> HORAS POR PROJETO
            </h3>
            {metrics.hours_by_project.length > 0 ? (
              <HBarChart data={metrics.hours_by_project} valueKey="hours" labelKey="project" color="#C8A400" suffix="h" />
            ) : (
              <p className="text-gray-400 text-sm font-mono text-center py-6">Nenhum dado disponivel</p>
            )}
          </div>

          {/* Top Pilots */}
          <div className="card-hud p-5 animate-slide-up" style={{ animationDelay: '0.35s' }}>
            <h3 className="text-navy font-rajdhani font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
              <Trophy size={18} className="text-amber-500" /> RANKING PILOTOS
            </h3>
            {metrics.top_pilots.length > 0 ? (
              <div className="space-y-1">
                {metrics.top_pilots.map((pilot, i) => (
                  <PilotRow key={i} pilot={pilot} rank={i + 1} />
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm font-mono text-center py-6">Nenhum piloto registrado</p>
            )}
          </div>
        </div>
      )}

      {/* ─── Secondary Charts ─── */}
      {showMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="metrics-secondary">
          {/* Shift Distribution */}
          <div className="card-hud p-5 animate-slide-up" style={{ animationDelay: '0.4s' }}>
            <h3 className="text-navy font-rajdhani font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
              <TrendingUp size={18} className="text-gold" /> VOOS POR TURNO
            </h3>
            <MiniBarChart data={metrics.shift_distribution} valueKey="count" labelKey="shift" color="#C8A400" />
          </div>

          {/* Daily Trend */}
          <div className="card-hud p-5 animate-slide-up" style={{ animationDelay: '0.45s' }}>
            <h3 className="text-navy font-rajdhani font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
              <Activity size={18} className="text-blue-500" /> ULTIMOS 7 DIAS
            </h3>
            <MiniBarChart data={metrics.daily_trend} valueKey="flights" labelKey="day" color="#4A90D9" />
          </div>
        </div>
      )}

      {/* ─── Expiring Alerts ─── */}
      {showMetrics && (metrics.expiring_drones?.length > 0 || metrics.expiring_projects?.length > 0) && (
        <div className="card-hud p-5 animate-slide-up border-red-200" style={{ animationDelay: '0.5s' }} data-testid="metrics-alerts">
          <h3 className="text-navy font-rajdhani font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
            <Shield size={18} className="text-red-500" /> ALERTAS DE VENCIMENTO
          </h3>
          <div className="space-y-2">
            {metrics.expiring_drones?.map((drone, i) => (
              <AlertItem key={`d-${i}`} item={drone} type="drone" />
            ))}
            {metrics.expiring_projects?.map((proj, i) => (
              <AlertItem key={`p-${i}`} item={proj} type="project" />
            ))}
          </div>
        </div>
      )}

      {/* ─── Non-metrics expiring alerts for non-admin roles ─── */}
      {!showMetrics && stats && (stats.expiring_drones?.length > 0 || stats.expiring_projects?.length > 0) && (
        <div className="card-hud p-4 animate-slide-up" style={{ animationDelay: '0.4s' }}>
          <h3 className="text-navy font-rajdhani font-semibold uppercase tracking-wider mb-4 flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-500" /> ALERTAS DE VENCIMENTO
          </h3>
          <div className="space-y-2">
            {stats.expiring_drones?.map((drone) => <AlertItem key={drone.id} item={drone} type="drone" />)}
            {stats.expiring_projects?.map((project) => <AlertItem key={project.id} item={project} type="project" />)}
          </div>
        </div>
      )}

      {/* ─── Streaming Section ─── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
            <Radio size={20} className="text-gold" /> STREAMING AO VIVO
          </h2>
          {isSuperAdmin && (
            <Button className="btn-primary text-xs" onClick={() => setShowAddStream(true)} data-testid="add-dashboard-stream-btn">
              <Plus size={16} className="mr-1" /> ADICIONAR STREAM
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="video-grid">
          {iframes.map((iframe, index) => (
            <VideoFrame
              key={iframe.id || index}
              iframe={iframe}
              index={index}
              onExpand={setExpandedVideo}
              onDelete={handleDeleteStream}
              projectName={iframe.projectName}
              canDelete={isSuperAdmin}
            />
          ))}
        </div>

        {iframes.length === 0 && (
          <div className="card-hud p-8 text-center">
            <Radio size={48} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 font-mono">Nenhum stream disponivel</p>
          </div>
        )}
      </div>

      {/* ─── Add Stream Modal ─── */}
      <Dialog open={showAddStream} onOpenChange={setShowAddStream}>
        <DialogContent className="bg-white border border-gray-200 max-w-md">
          <DialogHeader><DialogTitle className="text-navy font-rajdhani uppercase tracking-wider">ADICIONAR STREAM AO DASHBOARD</DialogTitle></DialogHeader>
          <form onSubmit={handleAddStream} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-gray-600 font-mono text-xs uppercase">Nome do Stream</Label>
              <Input value={newStreamName} onChange={(e) => setNewStreamName(e.target.value)} className="input-hud" placeholder="Ex: Camera 01" data-testid="new-stream-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-600 font-mono text-xs uppercase">URL do Stream *</Label>
              <Input value={newStreamUrl} onChange={(e) => setNewStreamUrl(e.target.value)} className="input-hud" placeholder="https://..." required data-testid="new-stream-url-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAddStream(false)} className="btn-secondary flex-1">CANCELAR</Button>
              <Button type="submit" className="btn-primary flex-1" data-testid="submit-add-stream-btn">ADICIONAR</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <FullscreenPlayer open={!!expandedVideo} onClose={() => setExpandedVideo(null)} videoUrl={expandedVideo?.url} videoName={expandedVideo?.name} />
    </div>
  );
};

export default DashboardPage;
